"use client";

import { IFeature } from "@clayton-homes/home-designer/api/common";
import DigitalModelCard from "@clayton-homes/home-designer/components/digital-model-card";
import { reducerCarousel } from "@clayton-homes/home-designer/components/gallery/carousel/reducer";
import {
	IUseSortableDigitalModelCriteria,
	IUseSortableDigitalModelOptions,
	useSortableDigitalModels,
} from "@clayton-homes/home-designer/hooks/use-sortable-digital-models";
import {
	IDigitalModel,
	IDigitalModelProductLine,
} from "@clayton-homes/home-designer/types/digital-model";
import formatPrice from "@clayton-homes/home-designer/util/format-price";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { AnalyticEventNames } from "../../utilities/env";
import { analytics } from "../../utilities/segment";
import { Button } from "../client.button";
import ModelError from "../client.model-error";

export interface IHomesDigitalModel
	extends IDigitalModel,
		IDigitalModelProductLine {
	includedFeatures?: IFeature[];
}

export interface PropsHomesGrid {
	criteria: IUseSortableDigitalModelCriteria;
	options?: IUseSortableDigitalModelOptions;
	digitalModelLinkType: "details" | "configure";
	galleryReducer?: typeof reducerCarousel;
	showBadges?: boolean;
	className?: string;
}

export function HomesGrid({
	criteria,
	options,
	digitalModelLinkType,
	galleryReducer = reducerCarousel,
	showBadges,
	className = "",
}: PropsHomesGrid) {
	const {
		data,
		error,
		isLoading,
		hasMore,
		loadMore,
		totalCount,
		chunkSize,
		currentChunk,
	} = useSortableDigitalModels(criteria, options);
	useEffect(() => {
		if (!error && !isLoading && !!data.length) {
			console.log("track dm set");
			analytics?.track(AnalyticEventNames.digitalModelListViewed, {
				plantNumbers: criteria.where.plantNumbers,
				pageNumber: currentChunk,
				pageTotalCount: Math.ceil(totalCount / chunkSize),
				selectedFeaturesCount: criteria.selectedFeatures?.length || 0,
				models: data.slice((currentChunk - 1) * chunkSize).map((model) => ({
					slug: model.slug,
					price: model.price,
					matchedFeatures: model.includedFeatures?.length,
				})),
			});
		}
	}, [data, error, criteria, totalCount, isLoading, currentChunk, chunkSize]);

	const searchParams = useSearchParams();
	const zipCode = searchParams?.get("zipCode");
	let digitalModels = data;
	if (isLoading) {
		return (
			<div className={className}>
				<HomesGridLoading homeCount={chunkSize} />
			</div>
		);
	}
	return (
		<div className={className}>
			{error ? (
				<ModelError apiError={error} nested={true} />
			) : (
				<>
					{showBadges ? (
						<p className="text-xs mb-4 font-bold uppercase text-primary-800">
							Match Results: {totalCount}
						</p>
					) : null}
					<div className="grid grid-cols-1 gap-x-9 gap-y-12 md:grid-cols-2 xl:grid-cols-3">
						{digitalModels
							? digitalModels.map((dm) => {
									return (
										<DigitalModelCard
											key={dm.slug}
											digitalModel={dm}
											analytics={analytics}
											className={
												"overflow-hidden rounded-lg bg-white no-underline"
											}
											as={Link}
											href={
												digitalModelLinkType === "details"
													? `/details/${dm.slug}?zipCode=${zipCode}`
													: `/configure?zipCode=${zipCode}&model=${dm.slug}`
											}
											onClick={() => {
												analytics?.track(
													AnalyticEventNames.digitalModelClicked,
													{
														modelSlug: dm.slug,
														basePrice: dm.price,
														plantNumbers: criteria.where.plantNumbers,
													},
												);
											}}
										>
											<DigitalModelCard.InfoContainer digitalModel={dm}>
												<div className="mt-0 w-full p-4">
													<div className="flex items-center justify-between gap-4 text-gray-600">
														<div>
															{showBadges ? (
																<span className="text-headline-6 rounded-full bg-gray-200 px-2 py-1 font-semibold uppercase">
																	{dm.includedFeatures?.length} Matched Feature
																	{dm.includedFeatures?.length !== 1 ? "s" : ""}
																</span>
															) : null}
															<h2 className="text-headline-4 m-0 mb-1 text-primary-800">
																{dm.name}
															</h2>
															<h3 className="text-headline-6 mb-2 font-semibold uppercase">
																{dm.productLine?.brandSeries?.name} Series
															</h3>
															<ul className="text-xs m-0 p-0 lg:text-caption">
																<li className="float-left list-none">
																	{dm.beds} beds
																</li>
																<li className="float-left list-none before:mx-2 before:font-bold before:text-gray-400 before:content-['\2022']">
																	{dm.baths} baths
																</li>
																<li className="float-left list-none before:mx-2 before:font-bold before:text-gray-400 before:content-['\2022']">
																	{dm.squareFeet} sq. ft.
																</li>
															</ul>
														</div>
														<div className="flex flex-col">
															<span className="text-headline-6 text-right font-semibold uppercase">
																Starting Price
															</span>
															<span className="text-headline-4 m-0 text-right text-primary-800">
																{formatPrice(dm?.price)}
															</span>
														</div>
													</div>
												</div>
											</DigitalModelCard.InfoContainer>
											<DigitalModelCard.Gallery
												className="relative"
												reducer={galleryReducer}
											>
												<DigitalModelCard.Gallery.Image
													as={Image}
													alt=""
													sizes={
														"(max-width: 767px) 90vw, (max-width: 1279px) 40vw, (max-width: 1439px) 30vw, 392px"
													}
													fill
												/>
												<DigitalModelCard.Gallery.ThumbnailsSection>
													{(props) => (
														<>
															<div className="absolute bottom-0 flex w-full items-center justify-center gap-2 self-end py-4">
																{props.state.items.map((value, index) => {
																	return (
																		<div
																			key={index}
																			className={`${index === props.state.currentPosition ? "h-3 w-3" : Math.abs(index - props.state.currentPosition) === 1 ? "h-[9px] w-[9px]" : Math.abs(index - props.state.currentPosition) > 2 ? "invisible absolute" : "h-[6px] w-[6px]"} rounded-full border border-gray-300 bg-white transition-all duration-300 ease-in`}
																		/>
																	);
																})}
															</div>
														</>
													)}
												</DigitalModelCard.Gallery.ThumbnailsSection>
											</DigitalModelCard.Gallery>
										</DigitalModelCard>
									);
								})
							: null}
					</div>
					<div className="mt-6 text-center">
						{!hasMore ? null : (
							<Button
								onPress={() => loadMore()}
								disabled={isLoading}
								className="w-96 max-w-full sm:w-44"
							>
								{isLoading ? "Loading..." : "Load More Homes"}
							</Button>
						)}
					</div>
				</>
			)}
		</div>
	);
}

export async function HomesGridLoading({
	homeCount = 6,
}: {
	homeCount?: number;
}) {
	return (
		<div className="grid grid-cols-1 gap-x-9 gap-y-12 md:grid-cols-2 xl:grid-cols-3">
			{new Array(homeCount).fill(0).map((_, i) => {
				return (
					<div key={i} className="flex w-full flex-col gap-y-6">
						<div className="h-60 w-full animate-pulse rounded-lg bg-gray-300 outline outline-gray-400"></div>
						<div className="flex justify-between">
							<span className="h-4 w-1/4 animate-pulse bg-gray-500" />
							<span className="h-4 w-1/4 animate-pulse bg-gray-300" />
						</div>
						<div className="-mt-4 flex justify-between">
							<span className="h-4 w-1/2 animate-pulse bg-gray-300" />
							<span className="h-4 w-1/4 animate-pulse bg-gray-500" />
						</div>
					</div>
				);
			})}
		</div>
	);
}

export default HomesGrid;
